import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as salesService from '@/services/salesService';
import type {
  SQFilter,
  SOFilter,
  CreateSQRequest,
  CreateSORequest,
  UpdateSQRequest,
  UpdateSORequest,
  SQStatus,
  SOStatus,
  SOItemStatus,
  Customer,
} from '@/types/salesorder';

// ============================================================================
// Query Keys
// ============================================================================

export const SQ_QUERY_KEYS = {
  all: ['salesQuotations'] as const,
  lists: () => [...SQ_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: SQFilter) => [...SQ_QUERY_KEYS.lists(), filter] as const,
  details: () => [...SQ_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...SQ_QUERY_KEYS.details(), id] as const,
  nextNumber: () => ['sq', 'nextNumber'] as const,
};

export const SO_QUERY_KEYS = {
  all: ['salesOrders'] as const,
  lists: () => [...SO_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: SOFilter) => [...SO_QUERY_KEYS.lists(), filter] as const,
  details: () => [...SO_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...SO_QUERY_KEYS.details(), id] as const,
  nextNumber: () => ['so', 'nextNumber'] as const,
};

export const CUSTOMER_QUERY_KEYS = {
  all: ['customers'] as const,
};

// ============================================================================
// Sales Quotation Hooks
// ============================================================================

/**
 * Hook to fetch list of sales quotations with optional filtering
 */
export function useSalesQuotations(filter?: SQFilter) {
  return useQuery(
    SQ_QUERY_KEYS.list(filter),
    () => salesService.listSQs(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
}

/**
 * Hook to fetch a single sales quotation by ID
 */
export function useSalesQuotation(id: string | undefined) {
  return useQuery(
    SQ_QUERY_KEYS.detail(id!),
    () => salesService.getSQ(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to create a new sales quotation
 */
export function useCreateSalesQuotation() {
  const queryClient = useQueryClient();

  return useMutation(
    (request: CreateSQRequest) => salesService.createSQ(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(SQ_QUERY_KEYS.lists());
        queryClient.invalidateQueries(SQ_QUERY_KEYS.nextNumber());
      },
    }
  );
}

/**
 * Hook to update an existing sales quotation
 */
export function useUpdateSalesQuotation() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, request }: { id: string; request: UpdateSQRequest }) => salesService.updateSQ(id, request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(SQ_QUERY_KEYS.lists());
        queryClient.setQueryData(SQ_QUERY_KEYS.detail(variables.id), data);
      },
    }
  );
}

/**
 * Hook to update sales quotation status
 */
export function useUpdateSQStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status }: { id: string; status: SQStatus }) => salesService.updateSQStatus(id, status),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(SQ_QUERY_KEYS.lists());
        queryClient.invalidateQueries(SQ_QUERY_KEYS.detail(variables.id));
      },
    }
  );
}

/**
 * Hook to delete a sales quotation
 */
export function useDeleteSalesQuotation() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => salesService.deleteSQ(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(SQ_QUERY_KEYS.lists());
      },
    }
  );
}

/**
 * Hook to accept a quotation and convert to sales order
 */
export function useAcceptQuotation() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => salesService.acceptQuotation(id),
    {
      onSuccess: (_, id) => {
        // Invalidate both SQ and SO queries
        queryClient.invalidateQueries(SQ_QUERY_KEYS.lists());
        queryClient.invalidateQueries(SQ_QUERY_KEYS.detail(id));
        queryClient.invalidateQueries(SO_QUERY_KEYS.lists());
        queryClient.invalidateQueries(SO_QUERY_KEYS.nextNumber());
      },
    }
  );
}

// ============================================================================
// Sales Order Hooks
// ============================================================================

/**
 * Hook to fetch list of sales orders with optional filtering
 */
export function useSalesOrders(filter?: SOFilter) {
  return useQuery(
    SO_QUERY_KEYS.list(filter),
    () => salesService.listSOs(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
}

/**
 * Hook to fetch a single sales order by ID
 */
export function useSalesOrder(id: string | undefined) {
  return useQuery(
    SO_QUERY_KEYS.detail(id!),
    () => salesService.getSO(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to create a new sales order
 */
export function useCreateSalesOrder() {
  const queryClient = useQueryClient();

  return useMutation(
    (request: CreateSORequest) => salesService.createSO(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(SO_QUERY_KEYS.lists());
        queryClient.invalidateQueries(SO_QUERY_KEYS.nextNumber());
      },
    }
  );
}

/**
 * Hook to update an existing sales order
 */
export function useUpdateSalesOrder() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, request }: { id: string; request: UpdateSORequest }) => salesService.updateSO(id, request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(SO_QUERY_KEYS.lists());
        queryClient.setQueryData(SO_QUERY_KEYS.detail(variables.id), data);
      },
    }
  );
}

/**
 * Hook to update sales order status
 */
export function useUpdateSOStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status }: { id: string; status: SOStatus }) => salesService.updateSOStatus(id, status),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(SO_QUERY_KEYS.lists());
        queryClient.invalidateQueries(SO_QUERY_KEYS.detail(variables.id));
      },
    }
  );
}

/**
 * Hook to update line item status
 */
export function useUpdateSOItemStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      soId,
      lineNo,
      status,
      shippedQty,
    }: {
      soId: string;
      lineNo: number;
      status: SOItemStatus;
      shippedQty?: number;
    }) => salesService.updateSOItemStatus(soId, lineNo, { status, shipped_qty: shippedQty }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(SO_QUERY_KEYS.lists());
        queryClient.invalidateQueries(SO_QUERY_KEYS.detail(variables.soId));
      },
    }
  );
}

/**
 * Hook to delete a sales order
 */
export function useDeleteSalesOrder() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => salesService.deleteSO(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(SO_QUERY_KEYS.lists());
      },
    }
  );
}

// ============================================================================
// Customer Hooks
// ============================================================================

/**
 * Hook to fetch customers for dropdown selection
 */
export function useCustomers() {
  return useQuery<Customer[]>(
    CUSTOMER_QUERY_KEYS.all,
    () => salesService.getCustomers(),
    {
      staleTime: 60000, // Cache for 1 minute
      cacheTime: 300000, // Keep in cache for 5 minutes
    }
  );
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to get the next SQ number (preview only)
 */
export function useNextSQNumber() {
  return useQuery(
    SQ_QUERY_KEYS.nextNumber(),
    () => salesService.getNextSQNumber(),
    {
      staleTime: 5000, // Refresh frequently
    }
  );
}

/**
 * Hook to get the next SO number (preview only)
 */
export function useNextSONumber() {
  return useQuery(
    SO_QUERY_KEYS.nextNumber(),
    () => salesService.getNextSONumber(),
    {
      staleTime: 5000, // Refresh frequently
    }
  );
}
